import React, { useEffect, useState } from "react";
import { Button, Form, Table, Space, Popconfirm, notification } from "antd";

import { MultiLanguageInput } from "../../../Components/MultiLanguageInput";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useParams } from "react-router-dom";
import { IMultiLanguage } from "../../../types/MutliLanguage";
import {
  getMainWebsiteByID,
  pullKeywordFromMainWebsite,
  pushKeywordIntoMainWebsite,
} from "../actions";

interface Props {
  fetchData: () => void;
}

interface FormValues {
  keyword: {
    en: string;
    ar: string;
    fr: string;
  };
}

const Top10MainWebsitesKeywordManager: React.FC<Props> = ({ fetchData }) => {
  const { id } = useParams();
  const [form] = Form.useForm<FormValues>();
  const [loading, setLoading] = useState(false);
  const [api, contextHolder] = notification.useNotification();
  const [keywords, setKeywords] = useState<IMultiLanguage[]>([]);

  const fetchItem = async () => {
    setLoading(true);
    const ft = await getMainWebsiteByID(id!);
    if (ft) setKeywords(ft?.MainInfo.Keywords);
    setLoading(false);
  };

  useEffect(() => {
    fetchItem();
  }, [id]);
  const handleAdd = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);
      const result = await pushKeywordIntoMainWebsite(id!, values.keyword);
      if (result) {
        fetchData();
        fetchItem();
        form.resetFields();
        api.success({ message: "Keyword added successfully" });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to add keyword",
        description: err?.message || "Unexpected error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (kw: IMultiLanguage) => {
    try {
      setLoading(true);
      const result = await pullKeywordFromMainWebsite(id!, {
        en: kw.En,
        ar: kw.Ar,
        fr: kw.Fr,
      });
      if (result) {
        fetchData();
        fetchItem();
        api.success({ message: "Keyword removed successfully" });
      }
    } catch (err: any) {
      api.error({
        message: "Failed to remove keyword",
        description: err?.message || "Unexpected error",
      });
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: "EN",
      dataIndex: ["En"],
      key: "en",
    },
    {
      title: "AR",
      dataIndex: ["Ar"],
      key: "ar",
    },
    {
      title: "FR",
      dataIndex: ["Fr"],
      key: "fr",
    },
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: IMultiLanguage) => (
        <Popconfirm
          title="Remove this keyword?"
          onConfirm={() => handleDelete(record)}
          okText="Yes"
          cancelText="No"
        >
          <Button icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      {contextHolder}

      <Form form={form} layout="vertical" onFinish={handleAdd}>
        <MultiLanguageInput name="keyword" label="Add Keyword" />
        <Form.Item>
          <Button
            icon={<PlusOutlined />}
            type="primary"
            htmlType="submit"
            loading={loading}
            block
          >
            Add Keyword
          </Button>
        </Form.Item>
      </Form>

      <Table
        columns={columns}
        dataSource={keywords}
        rowKey={(record, index) => `${record.En}-${index}`}
        pagination={false}
        loading={loading}
      />
    </div>
  );
};

export default Top10MainWebsitesKeywordManager;
